import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import Modal from "@/components/base/Modal";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import Avatar from "@/components/base/Avatar";
import { useApi } from "@/hooks/useApi";
import { useNow } from "@/hooks/useNow";
import { useToast } from "@/components/feature/ToastProvider";
import { useAuth } from "@/components/feature/AuthProvider";
import type { RpsDetail, RpsHand } from "@/lib/types";
import { closeRps, deleteRps, playRps, type RpsPlayResult } from "../api";
import {
  HANDS,
  STATE_BADGE,
  STATE_BORDER,
  STATE_STRIP,
  formatCountdown,
  formatDeadline,
  handLabel,
  isDim,
  stateLabel,
} from "../utils";
import HandPickButtons from "../components/HandPickButtons";
import HandReveal from "../components/HandReveal";
import RpsResultList from "../components/RpsResultList";

// /rps/:no — 상세 + 참여
export default function RpsDetailPage() {
  const { no } = useParams();
  const { toast } = useToast();
  const { member, requireLogin } = useAuth();
  const now = useNow();

  const { data, loading, error, reload, serverTs } = useApi<RpsDetail>(
    no ? `rps.php?no=${no}` : null
  );

  const [item, setItem] = useState<RpsDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmHand, setConfirmHand] = useState<RpsHand | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [reveal, setReveal] = useState<
    (RpsPlayResult & { myHand: RpsHand }) | null
  >(null);
  const reloadedRef = useRef(false);

  useEffect(() => {
    if (data) {
      setItem(data);
      setConfirmDel(false);
      reloadedRef.current = false;
    }
  }, [data]);

  const elapsed = serverTs ? Math.max(0, now / 1000 - serverTs) : 0;
  const remaining = Math.max(0, (item?.end_in ?? 0) - elapsed);

  // 마감 시간이 되면 목록을 다시 불러온다 (서버가 그때 자동 마감 처리).
  useEffect(() => {
    if (item?.state === "open" && remaining === 0 && !reloadedRef.current) {
      reloadedRef.current = true;
      reload();
    }
  }, [item?.state, remaining, reload]);

  const doPlay = useCallback(
    async (hand: RpsHand) => {
      if (!no) return;
      setBusy(true);
      try {
        const res = await playRps(Number(no), hand);
        const d = res.data ?? {};
        if (d.closed === 1) {
          // 내가 마지막 자리를 채웠다 → 결과 연출을 띄운다.
          setReveal({ ...d, myHand: hand });
        } else {
          toast(res.msg || "손을 냈습니다. 결과는 마감되면 공개됩니다.", "success");
          reload();
        }
      } catch (e) {
        toast(e instanceof Error ? e.message : "참여에 실패했습니다.", "error");
        reload();
      } finally {
        setBusy(false);
      }
    },
    [no, toast, reload]
  );

  const doClose = useCallback(async () => {
    if (!no) return;
    setBusy(true);
    try {
      const res = await closeRps(Number(no));
      toast(res.msg || "마감했습니다. 결과가 공개됩니다.", "success");
      reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "마감에 실패했습니다.", "error");
    } finally {
      setBusy(false);
    }
  }, [no, toast, reload]);

  const doDelete = useCallback(async () => {
    if (!no) return;
    setBusy(true);
    try {
      const res = await deleteRps(Number(no));
      toast(res.msg || "삭제했습니다.", "success");
      if (typeof window.REACT_APP_NAVIGATE === "function") {
        window.REACT_APP_NAVIGATE("/rps");
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : "삭제에 실패했습니다.", "error");
      setConfirmDel(false);
      setBusy(false);
    }
  }, [no, toast]);

  const openConfirm = (hand: RpsHand) => {
    if (!member) {
      requireLogin(`/rps/${no ?? ""}`);
      return;
    }
    setConfirmHand(hand);
  };

  if (loading) {
    return (
      <>
        <PageBanner
          title="가위바위보"
          breadcrumb={[{ label: "가위바위보", to: "/rps" }, { label: "상세" }]}
        />
        <section className="mx-auto max-w-3xl space-y-4 px-4 py-10 md:px-6">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-56 w-full rounded-xl" />
        </section>
      </>
    );
  }

  if (error || !item) {
    return (
      <>
        <PageBanner
          title="가위바위보"
          breadcrumb={[{ label: "가위바위보", to: "/rps" }, { label: "상세" }]}
        />
        <section className="mx-auto max-w-3xl px-4 py-12 md:px-6">
          <EmptyState
            icon="ri-error-warning-line"
            title="판을 불러오지 못했습니다"
            description={error ?? "존재하지 않는 판입니다."}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        </section>
      </>
    );
  }

  const cd = formatCountdown(remaining);
  const unlimited = item.limit_cnt === 0;
  const left = item.left_cnt;
  const mine = item.is_mine === 1;
  const joined = item.is_joined === 1;
  const open = item.state === "open";
  const dim = isDim(item.state);
  const myHand = item.my?.hand ?? "";

  const btnBase =
    "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-bold transition-colors disabled:opacity-50";

  return (
    <>
      <PageBanner
        title="가위바위보"
        breadcrumb={[{ label: "가위바위보", to: "/rps" }, { label: "상세" }]}
      />

      <section className="mx-auto max-w-3xl px-4 py-8 md:px-6 md:py-12">
        <article
          className={`relative overflow-hidden rounded-xl border bg-background-50 ${
            STATE_BORDER[item.state]
          } ${dim ? "opacity-90" : ""}`}
        >
          <span
            className={`absolute inset-y-0 left-0 w-1.5 ${STATE_STRIP[item.state]}`}
            aria-hidden="true"
          ></span>

          <div className="p-6 pl-7 md:p-8 md:pl-9">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                  STATE_BADGE[item.state]
                }`}
              >
                {stateLabel(item.state, item.state_txt)}
              </span>
              {mine && (
                <span className="inline-block rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-900">
                  내가 연 판
                </span>
              )}
              {joined && !mine && (
                <span className="inline-block rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-700">
                  참여완료
                </span>
              )}
            </div>

            <h2 className="mt-4 flex items-start gap-2 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
              <span aria-hidden="true">✊</span>
              {item.subject}
            </h2>

            {/* 등록자 */}
            <div className="mt-4 flex items-center gap-3">
              <Avatar name={item.host?.name} photo={item.host?.photo} size={44} />
              <span className="text-sm text-foreground-600">
                <strong className="font-semibold text-foreground-900">
                  {item.host?.aho} {item.host?.name}
                </strong>{" "}
                님이 냈습니다
              </span>
            </div>

            {/* 보상 */}
            {item.has_reward === 1 && item.reward ? (
              <p className="mt-4 inline-flex items-center gap-2 rounded-lg bg-accent-50 px-4 py-2.5 text-sm font-semibold text-accent-900">
                <span aria-hidden="true">🎁</span>
                이긴 사람 : {item.reward}
              </p>
            ) : null}

            {/* 핵심 게임 영역 */}
            <div className="mt-5 rounded-2xl border border-background-200 bg-background-100 px-5 py-6">
              {open ? (
                <OpenArea
                  mine={mine}
                  joined={joined}
                  myHand={myHand}
                  countdown={cd}
                  unlimited={unlimited}
                  left={left}
                  joinCnt={item.join_cnt}
                  limitCnt={item.limit_cnt}
                  busy={busy}
                  onPick={openConfirm}
                  onClose={doClose}
                />
              ) : (
                <ClosedArea
                  hostHand={item.host_hand ?? ""}
                  hostHandTxt={item.host_hand_txt}
                  winCnt={item.win_cnt ?? 0}
                  deadline={item.deadline}
                />
              )}
            </div>

            {/* 마감 전 안내 */}
            {open && (
              <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-foreground-500">
                <i className="ri-lock-2-line" aria-hidden="true"></i>
                등록자와 참여자의 손은 마감될 때까지 서버도 내려주지 않습니다.
              </p>
            )}

            {/* 액션 */}
            <div className="mt-6 flex flex-wrap gap-2">
              {mine ? (
                <>
                  {open && (
                    <button
                      type="button"
                      onClick={doClose}
                      disabled={busy}
                      className={`${btnBase} bg-primary-500 text-background-50 hover:bg-primary-600`}
                    >
                      <i className="ri-stop-circle-line" aria-hidden="true"></i>
                      지금 마감하고 결과 공개
                    </button>
                  )}
                  {confirmDel ? (
                    <>
                      <button
                        type="button"
                        onClick={doDelete}
                        disabled={busy}
                        className={`${btnBase} bg-accent-500 text-primary-950 hover:bg-accent-600`}
                      >
                        삭제 확인
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDel(false)}
                        disabled={busy}
                        className={`${btnBase} border border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100`}
                      >
                        취소
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmDel(true)}
                      disabled={busy}
                      className={`${btnBase} border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100`}
                    >
                      <i className="ri-delete-bin-line" aria-hidden="true"></i>
                      삭제
                    </button>
                  )}
                  <span className="flex items-center px-1 text-xs text-foreground-400">
                    등록자는 자기 판에 참여하지 않습니다.
                  </span>
                </>
              ) : (
                <span className="flex items-center px-1 text-xs text-foreground-400">
                  참여와 결과는 위 카드에서 진행됩니다.
                </span>
              )}
            </div>
          </div>
        </article>

        {/* 결과 목록 (마감 후) */}
        {!open && (
          <div className="mt-8">
            <h3 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-list-ordered text-foreground-500" aria-hidden="true"></i>
              참여자 결과
              <span className="rounded-full bg-background-100 px-2.5 py-0.5 text-sm font-semibold text-foreground-600">
                {item.joins?.length ?? 0}명
              </span>
            </h3>
            <RpsResultList joins={item.joins ?? []} />
          </div>
        )}

        {/* 목록으로 */}
        <div className="mt-8 flex justify-center">
          <Link
            to="/rps"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            목록으로
          </Link>
        </div>
      </section>

      {/* 손 선택 확인창 */}
      <Modal
        open={confirmHand !== null}
        onClose={() => (busy ? undefined : setConfirmHand(null))}
        maxWidth="max-w-xs"
        labelledBy="rps-confirm-title"
      >
        <div className="px-5 py-6 text-center">
          <span className="text-4xl leading-none" aria-hidden="true">
            {HANDS.find((h) => h.code === confirmHand)?.emoji}
          </span>
          <h2
            id="rps-confirm-title"
            className="mt-3 font-heading text-lg font-bold text-foreground-950"
          >
            {handLabel(confirmHand ?? "")}를 내시겠습니까?
          </h2>
          <p className="mt-2 text-sm text-foreground-500">
            한 번 내면 바꿀 수 없습니다.
          </p>
          <div className="mt-5 flex gap-2">
            <button
              type="button"
              onClick={() => setConfirmHand(null)}
              disabled={busy}
              className="flex-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
            >
              취소
            </button>
            <button
              type="button"
              onClick={() => {
                const h = confirmHand;
                setConfirmHand(null);
                if (h) doPlay(h);
              }}
              disabled={busy}
              className="flex-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600 disabled:opacity-60"
            >
              {busy ? "내는 중…" : "네, 냅니다"}
            </button>
          </div>
        </div>
      </Modal>

      {/* 결과 연출 (마지막 자리를 채운 경우) */}
      {reveal && (
        <HandReveal
          hostHand={reveal.host_hand ?? ""}
          hostHandTxt={reveal.host_hand_txt}
          result={reveal.result ?? ""}
          resultTxt={reveal.result_txt}
          reward={item.reward}
          hasReward={item.has_reward === 1}
          onDone={() => {
            setReveal(null);
            reload();
          }}
        />
      )}
    </>
  );
}

interface OpenAreaProps {
  mine: boolean;
  joined: boolean;
  myHand: string;
  countdown: ReturnType<typeof formatCountdown>;
  unlimited: boolean;
  left: number;
  joinCnt: number;
  limitCnt: number;
  busy: boolean;
  onPick: (hand: RpsHand) => void;
  onClose: () => void;
}

// 진행중 영역: 등록자 / 참여자 / 일반 방문자에 따라 다르게 보여준다.
function OpenArea({
  mine,
  joined,
  myHand,
  countdown,
  unlimited,
  left,
  joinCnt,
  limitCnt,
  busy,
  onPick,
  onClose,
}: OpenAreaProps) {
  const people = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <span className="inline-flex items-center gap-1.5 text-sm text-foreground-600">
        <i className="ri-alarm-warning-line text-foreground-400" aria-hidden="true"></i>
        마감까지{" "}
        <strong
          className={`tabular-nums ${
            countdown.urgent ? "animate-pulse text-accent-700" : "text-foreground-950"
          }`}
        >
          {countdown.text}
        </strong>
      </span>
      <span className="inline-flex items-center gap-1.5 text-sm text-foreground-600">
        <i className="ri-group-line text-foreground-400" aria-hidden="true"></i>
        {unlimited ? (
          <>
            <strong className="text-foreground-900">{joinCnt}</strong>명 · 인원 제한 없음
          </>
        ) : (
          <>
            <strong className="text-foreground-900">{joinCnt}</strong> / {limitCnt}명
          </>
        )}
      </span>
    </div>
  );

  // 등록자 본인
  if (mine) {
    return (
      <div className="text-center">
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-background-50 px-3.5 py-2 text-sm font-semibold text-foreground-600">
          <i className="ri-lock-2-line text-foreground-400" aria-hidden="true"></i>
          손은 마감 후 공개
        </span>
        <p className="mt-3 text-sm text-foreground-600">
          등록자라 참여는 못 합니다. 마감하면 결과가 바로 공개됩니다.
        </p>
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="mt-4 inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-bold text-background-50 hover:bg-primary-600 disabled:opacity-60"
        >
          <i className="ri-stop-circle-line" aria-hidden="true"></i>
          지금 마감하고 결과 공개
        </button>
        <div className="mt-5">{people}</div>
      </div>
    );
  }

  // 이미 참여한 사람
  if (joined) {
    return (
      <div className="text-center">
        <p className="text-sm text-foreground-600">당신이 낸 손</p>
        <div className="mt-2 flex items-center justify-center gap-3">
          <span className="text-5xl leading-none" aria-hidden="true">
            {HANDS.find((h) => h.code === myHand)?.emoji ?? "🖐️"}
          </span>
          <span className="font-heading text-lg font-bold text-foreground-950">
            {handLabel(myHand)}
          </span>
        </div>
        <p className="mt-3 text-sm text-foreground-600">
          결과는 마감 후 공개됩니다. 조금만 기다려 주세요.
        </p>
        {left >= 1 && left <= 2 && (
          <p className="mt-2 text-sm font-bold text-accent-700">{left}자리 남음</p>
        )}
        <div className="mt-5">{people}</div>
      </div>
    );
  }

  // 아직 참여하지 않은 방문자
  return (
    <div>
      <p className="mb-3 text-center text-sm font-semibold text-foreground-700">
        낼 손을 고르세요
      </p>
      <HandPickButtons onPick={onPick} disabled={busy} />
      <p className="mt-3 text-center text-xs text-foreground-500">
        한 번 내면 바꿀 수 없습니다. 1인 1회.
      </p>
      <div className="mt-5">{people}</div>
    </div>
  );
}

interface ClosedAreaProps {
  hostHand: string;
  hostHandTxt?: string;
  winCnt: number;
  deadline?: string;
}

// 마감 영역: 등록자 손을 크게 보여준다.
function ClosedArea({ hostHand, hostHandTxt, winCnt, deadline }: ClosedAreaProps) {
  const def = HANDS.find((h) => h.code === hostHand);
  return (
    <div className="text-center">
      <p className="text-sm font-semibold text-foreground-500">등록자의 손</p>
      <div className="mt-2 flex items-center justify-center gap-3">
        <span className="text-5xl leading-none" aria-hidden="true">
          {def?.emoji ?? ""}
        </span>
        <span className="font-heading text-2xl font-extrabold text-foreground-950">
          {hostHandTxt || def?.label || ""}
        </span>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-sm font-bold text-accent-900">
          <i className="ri-trophy-line" aria-hidden="true"></i>
          이긴 사람 {winCnt}명
        </span>
        {deadline ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50 px-3 py-1 text-xs font-medium text-foreground-500">
            <i className="ri-time-line" aria-hidden="true"></i>
            {formatDeadline(deadline)} 마감
          </span>
        ) : null}
      </div>
    </div>
  );
}